import type { Poem } from '@maria-cavalheiro-website/shared-types'

import { POEMS, POEM_POSITION } from './poem-collection.ts'

export interface PoemNeighbours {
  readonly previous: Poem
  readonly next: Poem
}

/**
 * The neighbour of every poem, by ordinal, computed once.
 *
 * The sequence WRAPS: the last poem's next is the first. Wrapping means neither
 * link is ever absent, so there is no layout branch, no disabled state and no
 * missing control to explain.
 *
 * Exported as a pure function of its input rather than a closure over the module
 * collection — the same reason as `buildPoemGroups`: it makes the empty case
 * reachable from a test instead of a defensive branch nothing can execute.
 */
export function buildNeighbours(poems: readonly Poem[]): readonly PoemNeighbours[] {
  const [first] = poems

  if (first === undefined) {
    return []
  }

  // Total by construction: `reduce` with a seed yields the last element of a
  // non-empty list, so there is no indexed access to guard.
  const last = poems.reduce((_, poem) => poem, first)

  return poems.map((_, position) => ({
    previous: poems[position - 1] ?? last,
    next: poems[position + 1] ?? first,
  }))
}

const NEIGHBOURS = buildNeighbours(POEMS)

/**
 * O(1): one Map probe for the ordinal, one array read for the pair.
 *
 * NOT `POEMS.indexOf(poem)` — that is what the design does, and it is an O(n) scan
 * re-run on every render, which rules/architecture.md disallows.
 *
 * Returns `undefined` for a poem that is not in the published collection. The
 * route resolver makes that unreachable in the app, but a caller can still
 * construct one.
 */
export function poemNeighbours(poem: Poem): PoemNeighbours | undefined {
  const index = POEM_POSITION.get(poem.slug)

  return index === undefined ? undefined : NEIGHBOURS[index]
}
